import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, TextField } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

interface FormInputs {
  username: string;
  password: string;
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'primary.main',
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: 'primary.light',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.light',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'text.secondary',
    '&.Mui-focused': {
      color: 'primary.light',
    },
  },
  '& .MuiInputBase-input': {
    color: 'text.primary',
  },
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser, user, loading: authLoading } = useAuth();
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const { control, handleSubmit } = useForm<FormInputs>({
    defaultValues: { username: '', password: '' },
  });

  React.useEffect(() => {
    if (user && !authLoading) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const onSubmit = async (data: FormInputs) => {
    if (!data.username || !data.password) {
      setError('Username and password are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/auth/login',
        { username: data.username, password: data.password },
        { withCredentials: true }
      );

      if (response.data.success) {
        await refreshUser();
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Authentication failed');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 3,
        backgroundColor: 'background.default',
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        sx={{
          color: 'primary.light',
          fontWeight: 300,
          letterSpacing: 2,
        }}
      >
        Personal Dashboard
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
          width: { xs: '100%', sm: 350 },
          maxWidth: '90vw',
        }}
      >
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Username"
              error={!!error}
              disabled={loading}
              autoFocus
              fullWidth
              sx={textFieldSx}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="password"
              label="Password"
              error={!!error}
              helperText={error}
              disabled={loading}
              fullWidth
              sx={textFieldSx}
            />
          )}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            height: 48,
            fontSize: '1.1rem',
            fontWeight: 500,
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Login'}
        </Button>
      </Box>
    </Box>
  );
};

export default Login;
